//frontend/dispatcher-client/src/pages/personnel/PersonnelDashboardPage.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  FiAlertCircle,
  FiBarChart2,
  FiCalendar,
  FiClipboard,
  FiClock,
  FiPieChart,
  FiRefreshCw,
  FiSun,
  FiUsers,
  FiZap,
} from "react-icons/fi";

import usePersonnelDashboardData from "../../hooks/usePersonnelDashboardData";

import "../../styles/PersonnelDashboard.css";

const DISTRIBUTION_META = [
  {
    key:
      "working",
    className:
      "is-working",
  },
  {
    key:
      "vacation",
    className:
      "is-vacation",
  },
  {
    key:
      "absence",
    className:
      "is-absence",
  },
  {
    key:
      "off",
    className:
      "is-off",
  },
  {
    key:
      "holidayWork",
    className:
      "is-holiday-work",
  },
];

const DISTRIBUTION_COLORS = {
  working:
    "var(--personnel-dashboard-working)",
  vacation:
    "var(--personnel-dashboard-vacation)",
  absence:
    "var(--personnel-dashboard-absence)",
  off:
    "var(--personnel-dashboard-off)",
  holidayWork:
    "var(--personnel-dashboard-holiday-work)",
};

const getPersonKey = (
  person
) =>
  `${person?.personType}:${person?.personId}`;

const formatDate = (
  value,
  locale
) => {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",
      month:
        "2-digit",
      year:
        "numeric",
    }
  ).format(
    date
  );
};

const getInitials = (
  name
) => {
  const parts =
    String(
      name ||
        "?"
    )
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  if (
    parts.length ===
    1
  ) {
    return parts[0]
      .slice(
        0,
        1
      )
      .toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`
    .toUpperCase();
};

const capitalizeFirst = (
  value
) => {
  const text =
    String(
      value ||
        ""
    );

  if (!text) {
    return "";
  }

  return (
    text
      .charAt(
        0
      )
      .toUpperCase() +
    text.slice(
      1
    )
  );
};

function StatCard({
  icon: Icon,
  label,
  value,
  helper,
  tone,
}) {
  return (
    <article
      className={[
        "personnel-dashboard-stat",
        tone
          ? `is-${tone}`
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="personnel-dashboard-stat-icon">
        <Icon />
      </div>

      <div className="personnel-dashboard-stat-copy">
        <span>
          {label}
        </span>

        <strong>
          {value}
        </strong>

        {helper ? (
          <small>
            {helper}
          </small>
        ) : null}
      </div>
    </article>
  );
}

export default function PersonnelDashboardPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const now =
    new Date();

  const [
    mode,
    setMode,
  ] =
    useState(
      "YEAR"
    );

  const [
    year,
    setYear,
  ] =
    useState(
      now.getFullYear()
    );

  const [
    month,
    setMonth,
  ] =
    useState(
      now.getMonth()
    );

  const [
    typeFilter,
    setTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    personFilter,
    setPersonFilter,
  ] =
    useState(
      "ALL"
    );

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "pt-PT"
    );

  const {
    people,
    loading,
    refreshing,
    error,
    refresh,

    rows,
    totals,
    distribution,
    distributionTotal,
    timeSeries,
    insights,
  } =
    usePersonnelDashboardData({
      year,
      month,
      mode,
      locale,
    });

  const numberFormatter =
    useMemo(
      () =>
        new Intl.NumberFormat(
          locale
        ),
      [
        locale,
      ]
    );

  const percentFormatter =
    useMemo(
      () =>
        new Intl.NumberFormat(
          locale,
          {
            style:
              "percent",
            maximumFractionDigits:
              1,
          }
        ),
      [
        locale,
      ]
    );

  const monthFormatter =
    useMemo(
      () =>
        new Intl.DateTimeFormat(
          locale,
          {
            month:
              "long",
          }
        ),
      [
        locale,
      ]
    );

  const yearOptions =
    useMemo(
      () => {
        const current =
          now.getFullYear();

        return Array.from(
          {
            length:
              6,
          },
          (
            _,
            index
          ) =>
            current -
            4 +
            index
        );
      },
      []
    );

  const peopleOptions =
    useMemo(
      () =>
        people
          .filter(
            (
              person
            ) =>
              typeFilter ===
                "ALL" ||
              person.personType ===
                typeFilter
          )
          .sort(
            (
              a,
              b
            ) =>
              String(
                a.displayName ||
                  ""
              ).localeCompare(
                String(
                  b.displayName ||
                    ""
                ),
                locale
              )
          ),
      [
        people,
        typeFilter,
        locale,
      ]
    );

  const visibleRows =
    useMemo(
      () =>
        rows.filter(
          (
            row
          ) => {
            if (
              typeFilter !==
                "ALL" &&
              row.person?.personType !==
                typeFilter
            ) {
              return false;
            }

            if (
              personFilter !==
                "ALL" &&
              row.personKey !==
                personFilter
            ) {
              return false;
            }

            return true;
          }
        ),
      [
        rows,
        typeFilter,
        personFilter,
      ]
    );

  const maxPersonWorked =
    Math.max(
      1,
      ...rows.map(
        (
          row
        ) =>
          row.workedDays
      )
    );

  const donutStyle =
    useMemo(
      () => {
        if (
          distributionTotal <=
          0
        ) {
          return undefined;
        }

        let cursor =
          0;

        const segments =
          distribution
            .filter(
              (
                item
              ) =>
                item.count >
                0
            )
            .map(
              (
                item
              ) => {
                const start =
                  cursor;

                cursor +=
                  item.percentage *
                  100;

                return `${DISTRIBUTION_COLORS[item.key]} ${start}% ${cursor}%`;
              }
            );

        return {
          background:
            `conic-gradient(${segments.join(", ")})`,
        };
      },
      [
        distribution,
        distributionTotal,
      ]
    );

  const maxSeriesValue =
    Math.max(
      1,
      ...timeSeries.map(
        (
          item
        ) =>
          item.working +
          item.vacation +
          item.absence
      )
    );

  const insightsList =
    useMemo(
      () => {
        const result =
          [];

        if (
          insights.topWorker
        ) {
          result.push({
            key:
              "top-worker",

            icon:
              FiCalendar,

            text:
              t(
                "dashboard.insights.top_worker",
                {
                  name:
                    insights
                      .topWorker
                      .person
                      ?.displayName,

                  count:
                    insights
                      .topWorker
                      .workedDays,

                  defaultValue:
                    `${insights.topWorker.person?.displayName} soma ${insights.topWorker.workedDays} dias de trabalho`,
                }
              ),
          });
        }

        if (
          insights.topVacation
        ) {
          result.push({
            key:
              "top-vacation",

            icon:
              FiSun,

            text:
              t(
                "dashboard.insights.top_vacation",
                {
                  name:
                    insights
                      .topVacation
                      .person
                      ?.displayName,

                  count:
                    insights
                      .topVacation
                      .vacationDays,

                  defaultValue:
                    `${insights.topVacation.person?.displayName} gozou ${insights.topVacation.vacationDays} dias de férias`,
                }
              ),
          });
        }

        if (
          insights.topAbsence
        ) {
          result.push({
            key:
              "top-absence",

            icon:
              FiAlertCircle,

            text:
              t(
                "dashboard.insights.top_absence",
                {
                  name:
                    insights
                      .topAbsence
                      .person
                      ?.displayName,

                  count:
                    insights
                      .topAbsence
                      .absenceDays,

                  defaultValue:
                    `${insights.topAbsence.person?.displayName} registou ${insights.topAbsence.absenceDays} dias de ausência`,
                }
              ),
          });
        }

        if (
          insights.topOff
        ) {
          result.push({
            key:
              "top-off",

            icon:
              FiClock,

            text:
              t(
                "dashboard.insights.top_off",
                {
                  name:
                    insights
                      .topOff
                      .person
                      ?.displayName,

                  count:
                    insights
                      .topOff
                      .offDays,

                  defaultValue:
                    `${insights.topOff.person?.displayName} teve ${insights.topOff.offDays} folgas planeadas`,
                }
              ),
          });
        }

        if (
          insights.mostPending
        ) {
          result.push({
            key:
              "pending",

            icon:
              FiClipboard,

            text:
              t(
                "dashboard.insights.pending",
                {
                  name:
                    insights
                      .mostPending
                      .person
                      ?.displayName,

                  count:
                    insights
                      .mostPending
                      .pendingRequests,

                  defaultValue:
                    `${insights.mostPending.person?.displayName} tem ${insights.mostPending.pendingRequests} pedidos pendentes`,
                }
              ),
          });
        }

        if (
          insights
            .peakVacationBucket
        ) {
          result.push({
            key:
              "peak-vacation",

            icon:
              FiBarChart2,

            text:
              t(
                "dashboard.insights.peak_vacation",
                {
                  period:
                    capitalizeFirst(
                      insights
                        .peakVacationBucket
                        .label
                    ),

                  count:
                    insights
                      .peakVacationBucket
                      .vacation,

                  defaultValue:
                    `${capitalizeFirst(
                      insights.peakVacationBucket.label
                    )} concentra mais férias (${insights.peakVacationBucket.vacation} dias)`,
                }
              ),
          });
        }

        if (
          insights
            .holidayWorkerCount >
          0
        ) {
          result.push({
            key:
              "holiday-workers",

            icon:
              FiClock,

            text:
              t(
                "dashboard.insights.holiday_workers",
                {
                  count:
                    insights
                      .holidayWorkerCount,

                  defaultValue:
                    `${insights.holidayWorkerCount} colaboradores têm trabalho confirmado em feriados`,
                }
              ),
          });
        }

        /*
         * Keep the card useful without making it taller
         * than the timeline beside it.
         */
        return result.slice(
          0,
          5
        );
      },
      [
        insights,
        t,
      ]
    );

  const handleTypeFilter = (
    value
  ) => {
    setTypeFilter(
      value
    );
    setPersonFilter(
      "ALL"
    );
  };

  return (
    <section className="personnel-page personnel-dashboard-page">
      <header className="personnel-dashboard-heading">
        <div className="personnel-dashboard-heading-copy">
          <h2>
            {t(
              "dashboard.title",
              {
                defaultValue:
                  "Dashboard do Personnel",
              }
            )}
          </h2>

          <p>
            {t(
              "dashboard.subtitle",
              {
                defaultValue:
                  "Visão geral da equipa, ausências e planeamento.",
              }
            )}
          </p>
        </div>

        <div className="personnel-dashboard-heading-tools">
          <div className="personnel-dashboard-period-toggle">
            <button
              type="button"
              className={
                mode ===
                "YEAR"
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setMode(
                  "YEAR"
                )
              }
            >
              {t(
                "dashboard.period.year",
                {
                  defaultValue:
                    "Anual",
                }
              )}
            </button>

            <button
              type="button"
              className={
                mode ===
                "MONTH"
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setMode(
                  "MONTH"
                )
              }
            >
              {t(
                "dashboard.period.month",
                {
                  defaultValue:
                    "Mensal",
                }
              )}
            </button>
          </div>

          <label className="personnel-dashboard-select">
            <FiCalendar />

            <select
              value={
                year
              }
              onChange={(
                event
              ) =>
                setYear(
                  Number(
                    event.target.value
                  )
                )
              }
            >
              {yearOptions.map(
                (
                  option
                ) => (
                  <option
                    key={
                      option
                    }
                    value={
                      option
                    }
                  >
                    {option}
                  </option>
                )
              )}
            </select>
          </label>

          <label
            className={[
              "personnel-dashboard-select",

              mode !==
              "MONTH"
                ? "is-disabled"
                : "",
            ]
              .filter(
                Boolean
              )
              .join(
                " "
              )}
          >
            <FiCalendar />

            <select
              value={
                month
              }
              onChange={(
                event
              ) =>
                setMonth(
                  Number(
                    event.target.value
                  )
                )
              }
              disabled={
                mode !==
                "MONTH"
              }
            >
              {Array.from(
                {
                  length:
                    12,
                },
                (
                  _,
                  index
                ) => (
                  <option
                    key={
                      index
                    }
                    value={
                      index
                    }
                  >
                    {monthFormatter.format(
                      new Date(
                        2026,
                        index,
                        1
                      )
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <button
            type="button"
            className="personnel-dashboard-refresh personnel-dashboard-refresh--icon"
            onClick={() =>
              refresh({
                silent:
                  true,
              })
            }
            disabled={
              refreshing
            }
            title={
              t(
                "dashboard.refresh",
                {
                  defaultValue:
                    "Atualizar",
                }
              )
            }
            aria-label={
              t(
                "dashboard.refresh",
                {
                  defaultValue:
                    "Atualizar",
                }
              )
            }
          >
            <FiRefreshCw
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />
          </button>
        </div>
      </header>

      {error ? (
        <div className="personnel-dashboard-warning">
          {t(
            "dashboard.refresh_warning",
            {
              defaultValue:
                "Não foi possível atualizar todos os dados. A informação em cache continua visível.",
            }
          )}
        </div>
      ) : null}

      <div className="personnel-dashboard-stats">
        <StatCard
          icon={
            FiCalendar
          }
          tone="working"
          label={
            t(
              "dashboard.stats.worked",
              {
                defaultValue:
                  "Dias de trabalho",
              }
            )
          }
          value={
            numberFormatter.format(
              totals.workedDays
            )
          }
          helper={
            t(
              "dashboard.stats.worked_help",
              {
                defaultValue:
                  "Planeamento concluído, descontando ausências aprovadas",
              }
            )
          }
        />

        <StatCard
          icon={
            FiSun
          }
          tone="vacation"
          label={
            t(
              "dashboard.stats.vacation",
              {
                defaultValue:
                  "Férias gozadas",
              }
            )
          }
          value={
            numberFormatter.format(
              totals.vacationDays
            )
          }
          helper={
            t(
              "dashboard.stats.vacation_help",
              {
                defaultValue:
                  "Apenas pedidos de férias aprovados",
              }
            )
          }
        />

        <StatCard
          icon={
            FiAlertCircle
          }
          tone="absence"
          label={
            t(
              "dashboard.stats.absence",
              {
                defaultValue:
                  "Ausências",
              }
            )
          }
          value={
            numberFormatter.format(
              totals.absenceDays
            )
          }
          helper={
            t(
              "dashboard.stats.absence_help",
              {
                defaultValue:
                  "Ausências aprovadas, excluindo férias",
              }
            )
          }
        />

        <StatCard
          icon={
            FiClock
          }
          tone="holiday-work"
          label={
            t(
              "dashboard.stats.holiday_work",
              {
                defaultValue:
                  "Trabalho em feriado",
              }
            )
          }
          value={
            numberFormatter.format(
              totals.holidayWorkedDays
            )
          }
          helper={
            t(
              "dashboard.stats.holiday_work_help",
              {
                defaultValue:
                  "Registos confirmados",
              }
            )
          }
        />

        <StatCard
          icon={
            FiClipboard
          }
          tone="pending"
          label={
            t(
              "dashboard.stats.pending",
              {
                defaultValue:
                  "Pedidos pendentes",
              }
            )
          }
          value={
            numberFormatter.format(
              totals.pendingRequests
            )
          }
          helper={
            t(
              "dashboard.stats.pending_help",
              {
                defaultValue:
                  "Pedidos ainda por decidir",
              }
            )
          }
        />
      </div>

      <div className="personnel-dashboard-grid personnel-dashboard-grid--primary">
        <article className="personnel-dashboard-card personnel-dashboard-people-card">
          <div className="personnel-dashboard-card-header">
            <div>
              <h3>
                {t(
                  "dashboard.people_summary.title",
                  {
                    defaultValue:
                      mode ===
                      "YEAR"
                        ? "Resumo anual por colaborador"
                        : "Resumo mensal por colaborador",
                  }
                )}
              </h3>

              <span>
                {t(
                  "dashboard.people_summary.subtitle",
                  {
                    defaultValue:
                      "Comparação rápida entre trabalho, férias e ausências.",
                  }
                )}
              </span>
            </div>

            <FiUsers />
          </div>

          <div className="personnel-dashboard-people-head">
            <span>
              {t(
                "dashboard.columns.person",
                {
                  defaultValue:
                    "Pessoa",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.worked",
                {
                  defaultValue:
                    "Trabalho",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.vacation",
                {
                  defaultValue:
                    "Férias",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.absence",
                {
                  defaultValue:
                    "Ausências",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.holiday_work",
                {
                  defaultValue:
                    "Feriados",
                }
              )}
            </span>
          </div>

          <div className="personnel-dashboard-people-list">
            {rows
              .slice(
                0,
                7
              )
              .map(
                (
                  row
                ) => (
                  <div
                    className="personnel-dashboard-person-row"
                    key={
                      row.personKey
                    }
                  >
                    <div className="personnel-dashboard-person-cell">
                      <span className="personnel-dashboard-avatar">
                        {getInitials(
                          row.person?.displayName
                        )}
                      </span>

                      <div>
                        <strong>
                          {row.person?.displayName ||
                            "—"}
                        </strong>

                        <div className="personnel-dashboard-person-bar">
                          <i
                            style={{
                              width:
                                row.workedDays >
                                0
                                  ? `${Math.max(
                                      4,
                                      row.workedDays /
                                        maxPersonWorked *
                                        100
                                    )}%`
                                  : "0%",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <strong>
                      {row.workedDays}
                    </strong>
                    <strong>
                      {row.vacationDays}
                    </strong>
                    <strong>
                      {row.absenceDays}
                    </strong>
                    <strong>
                      {row.holidayWorkedDays}
                    </strong>
                  </div>
                )
              )}

            {!loading &&
            rows.length ===
              0 ? (
              <div className="personnel-dashboard-empty">
                {t(
                  "dashboard.empty",
                  {
                    defaultValue:
                      "Ainda não existem dados para este período.",
                  }
                )}
              </div>
            ) : null}
          </div>
        </article>

        <article className="personnel-dashboard-card personnel-dashboard-distribution-card">
          <div className="personnel-dashboard-card-header">
            <div>
              <h3>
                {t(
                  "dashboard.distribution.title",
                  {
                    defaultValue:
                      "Distribuição de estados",
                  }
                )}
              </h3>

              <span>
                {t(
                  "dashboard.distribution.subtitle",
                  {
                    defaultValue:
                      "Estados registados no período.",
                  }
                )}
              </span>
            </div>

            <FiPieChart />
          </div>

          <div className="personnel-dashboard-distribution-body">
            <div
              className={[
                "personnel-dashboard-donut",
                distributionTotal <=
                0
                  ? "is-empty"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={
                donutStyle
              }
            >
              <div>
                <strong>
                  {numberFormatter.format(
                    distributionTotal
                  )}
                </strong>
                <span>
                  {t(
                    "dashboard.distribution.total",
                    {
                      defaultValue:
                        "estados",
                    }
                  )}
                </span>
              </div>
            </div>

            <div className="personnel-dashboard-distribution-list">
              {DISTRIBUTION_META.map(
                (
                  meta
                ) => {
                  const item =
                    distribution.find(
                      (
                        row
                      ) =>
                        row.key ===
                        meta.key
                    ) || {
                      count:
                        0,
                      percentage:
                        0,
                    };

                  return (
                    <div
                      className="personnel-dashboard-distribution-row"
                      key={
                        meta.key
                      }
                    >
                      <span
                        className={`personnel-dashboard-dot ${meta.className}`}
                      />

                      <span>
                        {t(
                          `dashboard.distribution.${meta.key}`,
                          {
                            defaultValue:
                              meta.key,
                          }
                        )}
                      </span>

                      <strong>
                        {numberFormatter.format(
                          item.count
                        )}
                      </strong>

                      <em>
                        {percentFormatter.format(
                          item.percentage
                        )}
                      </em>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </article>
      </div>

      <div className="personnel-dashboard-grid personnel-dashboard-grid--secondary">
        <article className="personnel-dashboard-card personnel-dashboard-timeline-card">
          <div className="personnel-dashboard-card-header">
            <div>
              <h3>
                {t(
                  "dashboard.timeline.title",
                  {
                    defaultValue:
                      mode ===
                      "YEAR"
                        ? "Linha temporal mensal"
                        : "Linha temporal semanal",
                  }
                )}
              </h3>

              <span>
                {t(
                  "dashboard.timeline.subtitle",
                  {
                    defaultValue:
                      "Trabalho, férias e ausências ao longo do período.",
                  }
                )}
              </span>
            </div>

            <FiBarChart2 />
          </div>

          <div className="personnel-dashboard-chart-legend">
            <span>
              <i className="is-working" />
              {t(
                "dashboard.distribution.working",
                {
                  defaultValue:
                    "Trabalho",
                }
              )}
            </span>
            <span>
              <i className="is-vacation" />
              {t(
                "dashboard.distribution.vacation",
                {
                  defaultValue:
                    "Férias",
                }
              )}
            </span>
            <span>
              <i className="is-absence" />
              {t(
                "dashboard.distribution.absence",
                {
                  defaultValue:
                    "Ausência",
                }
              )}
            </span>
          </div>

          <div
            className="personnel-dashboard-chart"
            style={{
              gridTemplateColumns:
                `repeat(${Math.max(1, timeSeries.length)}, minmax(0, 1fr))`,
            }}
          >
            {timeSeries.map(
              (
                item
              ) => {
                const total =
                  item.working +
                  item.vacation +
                  item.absence;

                const height =
                  total >
                  0
                    ? Math.max(
                        4,
                        total /
                          maxSeriesValue *
                          100
                      )
                    : 0;

                const workShare =
                  total >
                  0
                    ? item.working /
                      total *
                      100
                    : 0;

                const vacationShare =
                  total >
                  0
                    ? item.vacation /
                      total *
                      100
                    : 0;

                return (
                  <div
                    className="personnel-dashboard-chart-column"
                    key={
                      item.key
                    }
                  >
                    <div className="personnel-dashboard-chart-value">
                      {total >
                      0
                        ? total
                        : ""}
                    </div>

                    <div className="personnel-dashboard-chart-track">
                      <div
                        className="personnel-dashboard-chart-stack"
                        style={{
                          height:
                            `${height}%`,
                        }}
                      >
                        {total >
                        0 ? (
                          <>
                            <span
                              className="is-absence"
                              style={{
                                height:
                                  `${Math.max(
                                    0,
                                    100 -
                                      workShare -
                                      vacationShare
                                  )}%`,
                              }}
                            />

                            <span
                              className="is-vacation"
                              style={{
                                height:
                                  `${vacationShare}%`,
                              }}
                            />

                            <span
                              className="is-working"
                              style={{
                                height:
                                  `${workShare}%`,
                              }}
                            />
                          </>
                        ) : null}
                      </div>
                    </div>

                    <span className="personnel-dashboard-chart-label">
                      {item.label}
                    </span>
                  </div>
                );
              }
            )}
          </div>
        </article>

        <article className="personnel-dashboard-card personnel-dashboard-insights-card">
          <div className="personnel-dashboard-card-header">
            <div>
              <h3>
                {t(
                  "dashboard.insights.title",
                  {
                    defaultValue:
                      "Destaques rápidos",
                  }
                )}
              </h3>

              <span>
                {t(
                  "dashboard.insights.subtitle",
                  {
                    defaultValue:
                      "Pontos que merecem atenção imediata.",
                  }
                )}
              </span>
            </div>

            <FiZap />
          </div>

          <div className="personnel-dashboard-insights-list">
            {insightsList.length >
            0 ? (
              insightsList.map(
                (
                  item
                ) => {
                  const Icon =
                    item.icon;

                  return (
                    <div
                      key={
                        item.key
                      }
                      className="personnel-dashboard-insight"
                    >
                      <span>
                        <Icon />
                      </span>

                      <p>
                        {item.text}
                      </p>
                    </div>
                  );
                }
              )
            ) : (
              <div className="personnel-dashboard-empty personnel-dashboard-empty--compact">
                {t(
                  "dashboard.insights.empty",
                  {
                    defaultValue:
                      "Ainda não existem destaques suficientes para este período.",
                  }
                )}
              </div>
            )}
          </div>
        </article>
      </div>

      <article className="personnel-dashboard-card personnel-dashboard-table-card">
        <div className="personnel-dashboard-card-header personnel-dashboard-table-heading">
          <div>
            <h3>
              {t(
                "dashboard.table.title",
                {
                  defaultValue:
                    "Detalhe por pessoa",
                }
              )}
            </h3>

            <span>
              {t(
                "dashboard.table.subtitle",
                {
                  defaultValue:
                    "Consulta rápida dos principais indicadores individuais.",
                }
              )}
            </span>
          </div>

          <div className="personnel-dashboard-table-filters">
            <select
              value={
                personFilter
              }
              onChange={(
                event
              ) =>
                setPersonFilter(
                  event.target.value
                )
              }
            >
              <option value="ALL">
                {t(
                  "dashboard.filters.all_people",
                  {
                    defaultValue:
                      "Todos os colaboradores",
                  }
                )}
              </option>

              {peopleOptions.map(
                (
                  person
                ) => (
                  <option
                    key={
                      getPersonKey(
                        person
                      )
                    }
                    value={
                      getPersonKey(
                        person
                      )
                    }
                  >
                    {person.displayName}
                  </option>
                )
              )}
            </select>

            <select
              value={
                typeFilter
              }
              onChange={(
                event
              ) =>
                handleTypeFilter(
                  event.target.value
                )
              }
            >
              <option value="ALL">
                {t(
                  "dashboard.filters.everyone",
                  {
                    defaultValue:
                      "Utilizadores + Motoristas",
                  }
                )}
              </option>

              <option value="USER">
                {t(
                  "dashboard.filters.users",
                  {
                    defaultValue:
                      "Utilizadores",
                  }
                )}
              </option>

              <option value="DRIVER">
                {t(
                  "dashboard.filters.drivers",
                  {
                    defaultValue:
                      "Motoristas",
                  }
                )}
              </option>
            </select>
          </div>
        </div>

        <div className="personnel-dashboard-table-wrap">
          <div className="personnel-dashboard-table-head">
            <span>
              {t(
                "dashboard.columns.person",
                {
                  defaultValue:
                    "Pessoa",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.worked",
                {
                  defaultValue:
                    "Trabalho",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.vacation",
                {
                  defaultValue:
                    "Férias",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.absence",
                {
                  defaultValue:
                    "Ausências",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.off",
                {
                  defaultValue:
                    "Folgas",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.holiday_work",
                {
                  defaultValue:
                    "Feriados trabalhados",
                }
              )}
            </span>
            <span>
              {t(
                "dashboard.columns.next_absence",
                {
                  defaultValue:
                    "Próxima ausência",
                }
              )}
            </span>
          </div>

          {visibleRows.map(
            (
              row
            ) => (
              <div
                className="personnel-dashboard-table-row"
                key={
                  row.personKey
                }
              >
                <div className="personnel-dashboard-table-person">
                  <span className="personnel-dashboard-avatar">
                    {getInitials(
                      row.person?.displayName
                    )}
                  </span>

                  <div>
                    <strong>
                      {row.person?.displayName ||
                        "—"}
                    </strong>
                    <span>
                      {row.person?.personType ===
                      "DRIVER"
                        ? t(
                            "calendar.driver",
                            {
                              defaultValue:
                                "Motorista",
                            }
                          )
                        : t(
                            "calendar.user",
                            {
                              defaultValue:
                                "Utilizador",
                            }
                          )}
                    </span>
                  </div>
                </div>

                <strong>
                  {row.workedDays}
                </strong>
                <strong>
                  {row.vacationDays}
                </strong>
                <strong>
                  {row.absenceDays}
                </strong>
                <strong>
                  {row.offDays}
                </strong>
                <strong>
                  {row.holidayWorkedDays}
                </strong>
                <strong>
                  {formatDate(
                    row.nextAbsence?.startAt,
                    locale
                  )}
                </strong>
              </div>
            )
          )}

          {!loading &&
          visibleRows.length ===
            0 ? (
            <div className="personnel-dashboard-empty">
              {t(
                "dashboard.empty",
                {
                  defaultValue:
                    "Ainda não existem dados para este período.",
                }
              )}
            </div>
          ) : null}
        </div>
      </article>

      {loading ? (
        <div className="personnel-dashboard-loading">
          <FiRefreshCw className="is-spinning" />
          {t(
            "dashboard.loading",
            {
              defaultValue:
                "A carregar dashboard...",
            }
          )}
        </div>
      ) : null}
    </section>
  );
}